import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { array, string, number, bool, func } from 'prop-types';
import { useTokens } from '@nike/nike-design-system-components';
import join from 'url-join';
import { useLocation } from 'react-router';

import TableInfiniteScroll from './TableInfiniteScroll';
import { TableServerSideContext } from './TableServerSideContext';
import { usePrevious } from '../../hooks/usePrevious';

import http from '../../util/http';

import { getSortQueryParams, getFilterQueryParams, scrollbarWidth } from './helpers';

import TableInfiniteScrollStyles from './styles';
import useWindowSize from '../../hooks/useWindowSize';

const api2 = import.meta.env.VITE_API2_URL;

/*
 * Infinite-scroll table with capabilities for server-side sorting, filtering, and pagination.
 * When using this component, be sure to wrap it (or its parent) with TableServerSideContext.
 */
const TableServerSide = ({
  columns,
  fullHeight,
  includeRowSelect,
  itemSize,
  height,
  url,
  fullWidth,
  onDataChange,
  extraParams,
  formatResponseObjects
}) => {
  const {
    tableInstance,
    columnSortModifiers,
    columnFilterModifiers,
    setTotalResources,
    refreshData: refresh
  } = useContext(TableServerSideContext);

  const { sortBy, filters } = tableInstance;

  const sortQueryParams = getSortQueryParams(sortBy, columnSortModifiers);
  const filterQueryParams = getFilterQueryParams(filters, columnFilterModifiers);

  const defaultUrl = `${url}?count=100&anchor=0${sortQueryParams}${filterQueryParams}${extraParams}`;

  const [hasNextPage, setHasNextPage] = useState(true);
  const [nextUrl, setNextUrl] = useState(defaultUrl);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [items, setItems] = useState([]);

  const { pathname } = useLocation();

  const previousPathname = usePrevious(pathname);

  const tokens = useTokens();

  const handleError = () => {
    setItems([]);
    setHasNextPage(false);
    setIsNextPageLoading(false);
  };

  const loadNextPage = () => {
    if (!hasNextPage || !nextUrl) {
      return Promise.resolve();
    }

    setIsNextPageLoading(true);

    return http
      .get(`${join(api2, nextUrl)}`)
      .then(response => {
        const totalResources = response?.data?.pages?.totalResources;
        const nextPageUrl = response?.data?.pages?.next || '';
        const objects = response?.data?.objects;
        const hasNextPage = nextPageUrl !== '';

        setIsNextPageLoading(false);
        setHasNextPage(hasNextPage);
        setNextUrl(nextPageUrl);

        setItems([...items].concat(formatResponseObjects ? formatResponseObjects(objects) : objects));

        setTotalResources(totalResources);

        return objects;
      })
      .then(onDataChange)
      .catch(handleError);
  };

  const refreshData = () => {
    setIsNextPageLoading(true);

    return http
      .get(`${join(api2, defaultUrl)}`)
      .then(response => {
        const totalResources = response?.data?.pages?.totalResources;
        const nextPageUrl = response?.data?.pages?.next || '';
        const objects = response?.data?.objects;
        const hasNextPage = nextPageUrl !== '';

        setIsNextPageLoading(false);
        setHasNextPage(hasNextPage);
        setNextUrl(nextPageUrl);

        setItems(formatResponseObjects ? formatResponseObjects(objects) : objects);

        setTotalResources(totalResources);

        return objects;
      })
      .catch(handleError);
  };

  // Create a reference for the InfiniteLoader
  const infiniteLoaderRef = useRef(null);
  const containerRef = useRef(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    // Reset cached items when "sort" or "filter" changes
    if (hasMountedRef.current) {
      if (infiniteLoaderRef.current) {
        infiniteLoaderRef.current.resetloadMoreItemsCache();
      }
    }
    setNextUrl(defaultUrl);
    setItems([]);
    setHasNextPage(true); // shows the spinner when new items are loading
    hasMountedRef.current = true;
  }, [JSON.stringify(sortBy), JSON.stringify(filters), refresh]);

  useEffect(() => {
    if (filters && sortBy && pathname === previousPathname) {
      refreshData().then(onDataChange);
    }
  }, [JSON.stringify(sortBy), JSON.stringify(filters), refresh, pathname, previousPathname]);

  const { width } = useWindowSize();
  const scrollbarSize = useMemo(scrollbarWidth, [width]);

  return (
    <TableInfiniteScrollStyles
      ref={containerRef}
      designTokens={tokens}
      fullWidth={fullWidth}
      scrollbarSize={scrollbarSize}
    >
      <TableInfiniteScroll
        columns={columns}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        items={items}
        itemSize={itemSize}
        loadNextPage={loadNextPage}
        includeRowSelect={includeRowSelect}
        containerRef={containerRef}
        fullHeight={fullHeight}
        height={height}
        infiniteLoaderRef={infiniteLoaderRef}
      />
    </TableInfiniteScrollStyles>
  );
};

TableServerSide.propTypes = {
  columns: array.isRequired,
  fullHeight: bool,
  fullWidth: bool,
  includeRowSelect: bool,
  itemSize: number,
  height: number,
  url: string.isRequired,
  onDataChange: func,
  extraParams: string,
  formatResponseObjects: func
};

TableServerSide.defaultProps = {
  fullHeight: false,
  fullWidth: false,
  height: 500,
  includeRowSelect: false,
  itemSize: 70,
  onDataChange: () => {},
  extraParams: '',
  formatResponseObjects: null
};

export default TableServerSide;
